import { isAxiosError } from 'axios';
import { useAuthStore } from '../store/auth.store';
import { FinancialConfirmationCancelled } from './financial-authorization';
import { http } from './http';
import type { CreatePaymentPayload, Payment } from '../types';

type PendingPayment = CreatePaymentPayload & { requestId: string };

/**
 * Identité de la saisie envoyée par la fenêtre « Nouvel encaissement » (23/10/2026), avec la confirmation d'un doublon
 * soupçonné. Plus de preuve de confirmation de sécurité depuis le 28/10/2026 (J20, encaissement sans mot de passe ni
 * code) : `authorization`, facultative, n'est jointe que si un appelant en fournit une.
 */
export type ManualPaymentConfirmation = {
  requestId: string;
  authorization?: string;
  confirmDuplicate?: boolean;
};

/**
 * Corps exact de `POST /v1/payments` : la saisie et son identité, avec `confirmDuplicate` quand un doublon soupçonné
 * est confirmé (jamais gardé dans la saisie en attente). Seule fabrique du corps du défi de confirmation ET de la
 * requête : le serveur exige qu'ils soient identiques.
 */
export function manualPaymentBody(
  command: PendingPayment,
  confirmDuplicate = false,
): Record<string, unknown> {
  return JSON.parse(
    JSON.stringify(confirmDuplicate ? { ...command, confirmDuplicate: true } : command),
  );
}

export function manualPaymentScope() {
  const { user, activeSchoolId } = useAuthStore.getState();
  if (!user?.id || !activeSchoolId)
    throw new Error('Session et école requises.');
  return `${activeSchoolId}:${user.id}`;
}
function storageKey(candidateId?: string) {
  return `permio:payment-create:${manualPaymentScope()}:${candidateId ?? 'none'}`;
}

/** Aucune expiration automatique : une réponse perdue peut cacher un paiement validé. */
export function pendingManualPayment(
  candidateId?: string,
): PendingPayment | null {
  const saved = sessionStorage.getItem(storageKey(candidateId));
  return saved ? JSON.parse(saved) : null;
}

/** Une saisie en attente ne se reprend qu'avec son contenu original. */
function sameAsPending(previous: PendingPayment | null, payload: CreatePaymentPayload) {
  if (!previous) return;
  const { requestId: _, ...original } = previous;
  // Le formulaire reprend l'objet conservé ; un autre appelant ne peut en changer le contenu.
  if (JSON.stringify(original) !== JSON.stringify(payload))
    throw new Error(
      'Reprenez la saisie en attente avec son contenu original.',
    );
}

/**
 * Confirmation dans la fenêtre (23/10/2026) : identité de la saisie (celle de la saisie en attente, sinon une neuve)
 * et corps exact à faire confirmer. Rien n'est stocké avant l'envoi.
 */
export function prepareManualPayment(
  payload: CreatePaymentPayload,
  confirmDuplicate = false,
): { requestId: string; body: Record<string, unknown> } {
  const previous = pendingManualPayment(payload.candidateId);
  sameAsPending(previous, payload);
  const command = previous ?? { ...payload, requestId: crypto.randomUUID() };
  return { requestId: command.requestId, body: manualPaymentBody(command, confirmDuplicate) };
}

/**
 * `confirmation` : identité de la saisie donnée par la fenêtre « Nouvel encaissement » (la saisie en attente se
 * reprend sous la sienne) ; l'encaissement part sans mot de passe ni code (J20).
 */
export async function submitManualPayment(
  payload: CreatePaymentPayload,
  confirmation?: ManualPaymentConfirmation,
): Promise<Payment> {
  const key = storageKey(payload.candidateId);
  const previous = pendingManualPayment(payload.candidateId);
  // La saisie en attente ne se reprend que sous sa propre identité.
  if (confirmation && previous && previous.requestId !== confirmation.requestId)
    throw new Error('Reprenez la saisie en attente avec son contenu original.');
  const command = previous ?? {
    ...payload,
    requestId: confirmation?.requestId ?? crypto.randomUUID(),
  };
  sameAsPending(previous, payload);
  const serialized = JSON.stringify(command);
  // Échec du stockage = aucun envoi : l'identité de reprise doit survivre au rechargement.
  sessionStorage.setItem(key, serialized);
  try {
    const { data } = await http.post<Payment>(
      '/v1/payments',
      manualPaymentBody(command, confirmation?.confirmDuplicate),
      confirmation?.authorization
        ? { headers: { 'x-fiscal-authorization': confirmation.authorization } }
        : undefined,
    );
    if (!data || typeof data.id !== 'string' || !data.id)
      throw new Error('Réponse de paiement incomplète : reprendre la demande conservée.');
    if (sessionStorage.getItem(key) === serialized)
      sessionStorage.removeItem(key);
    return data;
  } catch (error) {
    // Un abandon de la nouvelle confirmation, un refus de validation initial ou un doublon soupçonné à la première
    // saisie (vu à l'envoi depuis J20, plus à la confirmation) ne crée rien sous cette identité neuve.
    // Sur une reprise, même un refus ne résout pas l'incertitude du premier envoi.
    if (
      !previous &&
      (error instanceof FinancialConfirmationCancelled ||
        (isAxiosError(error) &&
          (error.response?.status === 400 ||
            (error.response?.status === 409 &&
              error.response.data?.code === 'PAYMENT_DUPLICATE_SUSPECTED'))))
    ) {
      if (sessionStorage.getItem(key) === serialized)
        sessionStorage.removeItem(key);
    }
    throw error;
  }
}

/**
 * Abandon d'une saisie en attente (fenêtre « Nouvel encaissement », 23/10/2026), proposé seulement après une réponse
 * du serveur à sa reprise qui prouve que rien n'est enregistré sous son identité (400, ou 409 doublon) : l'entrée
 * n'est retirée que si c'est bien celle-là. `true` si elle l'a été.
 */
export function abandonPendingManualPayment(
  candidateId: string | undefined,
  requestId: string,
): boolean {
  try {
    const key = storageKey(candidateId);
    const saved = JSON.parse(sessionStorage.getItem(key) ?? 'null') as { requestId?: string } | null;
    if (!saved || saved.requestId !== requestId) return false;
    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * F33 : clé de reprise d'une annulation ou d'une correction. Tant que la réponse du serveur n'est pas
 * connue (coupure, panne), la même demande garde sa clé dans l'onglet : renvoyée, elle rend son
 * résultat au lieu de « déjà annulé ». Une réponse du serveur, même un refus, clôt la demande.
 */
export async function sendWithReplayKey<T>(
  operation: string,
  content: unknown,
  send: (requestId: string) => Promise<T>,
): Promise<T> {
  const key = `permio:payment-${operation}:${manualPaymentScope()}`;
  const serialized = JSON.stringify(content);
  let saved: { requestId?: string; content?: string } | null = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(key) ?? 'null');
  } catch {
    saved = null;
  }
  const requestId =
    saved?.content === serialized && saved.requestId
      ? saved.requestId
      : crypto.randomUUID();
  const forget = () => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* stockage indisponible : rien à effacer */
    }
  };
  try {
    sessionStorage.setItem(key, JSON.stringify({ requestId, content: serialized }));
  } catch {
    /* stockage indisponible : la demande part sans reprise possible après rechargement */
  }
  try {
    const result = await send(requestId);
    forget();
    return result;
  } catch (error) {
    if (
      error instanceof FinancialConfirmationCancelled ||
      (isAxiosError(error) && error.response && error.response.status < 500)
    )
      forget();
    throw error;
  }
}
